import React from 'react';
import { Box, Typography } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, LabelList } from 'recharts';
import { COLORS } from '../../pages/app/reports/groups-teams-activity/constants';
import { FeatureUsagePoint } from '../../pages/app/reports/groups-teams-activity/types';

interface FeatureBarChartProps {
    data: FeatureUsagePoint[];
    title: string;
    subtitle: string;
}

const CustomLabel = (props: any) => {
    const { x, y, width, value } = props;
    return (
        <text
            x={x + width / 2}
            y={y - 6}
            fill={COLORS.textSecondary}
            fontSize={11}
            fontWeight={700}
            textAnchor="middle"
            fontFamily="Poppins, sans-serif"
        >
            {value?.toLocaleString()}
        </text>
    );
};

const CustomTick = (props: any) => {
    const { x, y, payload } = props;
    const lines = payload.value.split('\n');
    return (
        <g>
            {lines.map((line: string, i: number) => (
                <text
                    key={i}
                    x={x}
                    y={y + 12 + i * 11}
                    fill={COLORS.grayDark}
                    fontSize={9}
                    textAnchor="middle"
                    fontFamily="Poppins, sans-serif"
                >
                    {line}
                </text>
            ))}
        </g>
    );
};

export const FeatureBarChart: React.FC<FeatureBarChartProps> = ({ data, title, subtitle }) => {
    return (
        <Box
            sx={{
                backgroundColor: COLORS.cardBg,
                borderRadius: '10px',
                border: `1px solid ${COLORS.border}`,
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 18px',
                    borderBottom: `1px solid ${COLORS.borderLight}`,
                }}
            >
                <Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{title}</Typography>
                    <Typography sx={{ fontSize: '11px', color: COLORS.grayMedium }}>{subtitle}</Typography>
                </Box>
            </Box>

            {/* Chart */}
            <Box sx={{ padding: '16px 18px', height: 180 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} margin={{ top: 20, right: 16, left: 16, bottom: 30 }}>
                        <XAxis
                            dataKey="feature"
                            axisLine={false}
                            tickLine={false}
                            tick={CustomTick}
                            interval={0}
                        />
                        <YAxis hide />
                        <Bar
                            dataKey="count"
                            radius={[4, 4, 0, 0]}
                            maxBarSize={60}
                        >
                            {data.map((entry, index) => (
                                <Cell key={index} fill={entry.color} />
                            ))}
                            <LabelList dataKey="count" content={CustomLabel} />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
};
